<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable"></report-tools>

    <report-state v-if="loading" kind="loading"></report-state>
    <report-state
      v-else-if="!rows.length"
      icon="mdi-bowl-mix-outline"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <ui-table
      v-else
      variant="report"
      :columns="columns"
      :rows="rows"
      :row-key="(row) => row.key"
      :sort="sort"
      :totals="totals"
      :total-label="$t('total')"
      @sort="sortBy"
    >
      <template #head-shift="{ column }">
        <report-shift-tag :shift="column.shift.id"></report-shift-tag>
      </template>

      <template #cell-total="{ row }"><b>{{ row.total }}</b></template>

      <template #cell-share="{ row }">
        <span class="rd-share">
          <span class="rd-mini"><i :style="{ width: `${row.relative}%` }"></i></span>
          <span class="rd-muted rd-num">{{ row.share }}%</span>
        </span>
      </template>
    </ui-table>
  </div>
</template>

<script>
import CompanyReportTabMeals from "@/components/company/reports/companyTabs/companyReportTabMeals.vue";
import { SHIFTS } from "@/constants/shifts";
import hasBarShifts from "@/components/report/shared/hasBarShifts";
import hasShiftSubtitle from "@/components/report/shared/hasShiftSubtitle";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import hasTableSort from "@/components/report/desktop/hasTableSort";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import { hasPeriodRefetch } from "../../hasPeriodRefetch";

/**
 * Obroci firme na računaru: obrok × smena sa udjelom u ukupnom. Firme, smene, period i pretragu čita iz
 * zajedničke trake. Nasleđuje učitavanje, štampu i Excel iz starog taba.
 */
export default {
  name: "CompanyDesktopMeals",
  extends: CompanyReportTabMeals,
  mixins: [hasBarShifts, hasShiftSubtitle, hasTableSort, hasPeriodRefetch("fetchMeals")],
  components: { ReportShiftTag, ReportState, ReportTools },
  computed: {
    /** Kolone tabele: iste služe za zaglavlje, redove i zbirni red. */
    columns() {
      return [
        { key: "#", label: "#", index: true },
        { key: "name", label: this.$t("meal"), name: true, sortable: true },
        ...this.visibleShifts.map((shift) => ({ key: shift.key, slot: "shift", shift, align: "end", num: true, zero: true, sortable: true })),
        { key: "total", label: this.$t("total"), align: "end", num: true, sortable: true },
        { key: "share", label: this.$t("report_desktop.share"), cls: "rd-col-share" },
      ];
    },
    /** Smene 1–3 uvek (ako su izabrane), četvrta samo kad ima obroka. */
    visibleShifts() {
      return SHIFTS.filter(
        (shift) =>
          this.selectedShifts.includes(shift.id) &&
          (shift.id < 4 || this.filteredMeals.some((meal) => (meal[`shift${shift.id}`]?.quantity || 0) > 0))
      );
    },
    rows() {
      const found = this.search
        ? this.customSearch(this.filteredMeals, [{ value: "name" }], this.search)
        : this.filteredMeals;
      const visible = found.filter((meal) => meal.total > 0);
      const grand = visible.reduce((sum, meal) => sum + meal.total, 0);
      const max = Math.max(1, ...visible.map((meal) => meal.total));
      return this.sortRows(
        visible.map((meal, index) => {
          const row = {
            key: `${meal.id || meal.name}-${index}`,
            name: meal.name,
            total: meal.total,
            share: grand ? Math.round((meal.total / grand) * 100) : 0,
            relative: Math.round((meal.total / max) * 100),
          };
          SHIFTS.forEach((shift) => {
            row[shift.key] = meal[`shift${shift.id}`]?.quantity || 0;
          });
          return row;
        })
      );
    },
    totals() {
      const totals = { total: this.rows.reduce((sum, row) => sum + row.total, 0) };
      SHIFTS.forEach((shift) => {
        totals[shift.key] = this.rows.reduce((sum, row) => sum + row[shift.key], 0);
      });
      return totals;
    },
    emptyTitle() {
      return this.search
        ? this.$t("report_mobile.no_results_for", { search: this.search })
        : this.$t("report_mobile.no_meals");
    },
    emptyText() {
      return this.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        { key: "total", label: this.$t("report_desktop.total_meals"), value: this.totals.total, tone: "inverse" },
        { key: "meals", label: this.$t("meals"), value: this.rows.length },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    async copyTable() {
      const headers = [this.$t("meal"), ...this.visibleShifts.map((shift) => this.$t(shift.label)), this.$t("total")];
      const rows = this.rows.map((row) => [
        row.name,
        ...this.visibleShifts.map((shift) => row[shift.key]),
        row.total,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
